import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { blogApi } from "../api/blogApi";
import { useAuth } from "../auth/authStore";
import { ensureAbsUrl, getSiteOrigin } from "../utils/seo";
import { sanitizeExternalHttpUrl, sanitizeLinkHref } from "../utils/safeUrl";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";
import ReportContentButton from "../components/reports/ReportContentButton";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { DEFAULT_REACTION_KIND, REACTION_OPTIONS, getReactionEmoji } from "../ui/reactions/reactionKinds";
import { normalizeReactionState, normalizeReactionKindForApi } from "../ui/reactions/reactionModel";

const DEFAULT_COMMENT_GRADIENTS = [
  "linear-gradient(135deg,#085041,#5DCAA5)",
  "linear-gradient(135deg,#854F0B,#FAC775)",
  "linear-gradient(135deg,#72243e,#ed93b1)",
  "linear-gradient(135deg,#2f4a8f,#7f9fff)",
];

function formatDate(dt) {
  if (!dt) return "";
  return new Date(dt)
    .toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Moscow" })
    .replace(" г.", "");
}

function formatShortDate(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "Europe/Moscow" }).replace(".", "");
}

function formatDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Moscow",
    })
    .replace(" г.,", ",");
}

function stripToText(html) {
  if (typeof document === "undefined") return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const div = document.createElement("div");
  div.innerHTML = String(html || "");
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

function calcReadingMinutes(text) {
  const words = String(text || "")
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 180));
}

function getInitials(source) {
  const parts = String(source || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "БД";
  return parts.map((part) => part[0]).join("").toUpperCase();
}

function hashToGradient(seed, fallbackIndex = 0) {
  const source = String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) hash = (hash * 31 + source.charCodeAt(i)) | 0;
  const index = Math.abs(hash || fallbackIndex) % DEFAULT_COMMENT_GRADIENTS.length;
  return DEFAULT_COMMENT_GRADIENTS[index];
}

function sumReactionCounts(counts = {}) {
  return Object.values(counts || {}).reduce((total, value) => {
    const n = Number(value);
    return total + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

function normalizeCommentReactionState(item = {}) {
  const baseState = normalizeReactionState(item, DEFAULT_REACTION_KIND);
  const fallbackCount = Math.max(
    0,
    Number(
      item?.reactionsCount ??
        item?.likesCount ??
        item?.reactionCount ??
        item?.likes ??
        item?.count ??
        0
    ) || 0
  );
  const fallbackActive = Boolean(
    item?.likedByMe ??
      item?.liked ??
      item?.active ??
      item?.reacted ??
      item?.selected ??
      item?.mine ??
      item?.me
  );
  const fallbackKind = normalizeReactionKindForApi(
    item?.myReactionKind ??
      item?.reactionKindByMe ??
      item?.myReactionType ??
      item?.myReaction ??
      item?.viewerReaction ??
      item?.selectedReaction ??
      DEFAULT_REACTION_KIND,
    DEFAULT_REACTION_KIND
  );
  const counts = { ...(baseState.counts || {}) };

  if (!sumReactionCounts(counts) && fallbackCount) {
    counts[fallbackKind] = fallbackCount;
  }

  return {
    counts,
    activeType: baseState.activeType || (fallbackActive ? fallbackKind : ""),
  };
}

function buildCommentReactionView(comment = {}) {
  const reactionState = comment.reactionState || { counts: {}, activeType: "" };
  const activeKind = normalizeReactionKindForApi(reactionState.activeType || comment.myReactionKind || "", "");
  const total = sumReactionCounts(reactionState.counts) || Math.max(0, Number(comment.likes || 0) || 0);
  return {
    activeKind,
    emoji: activeKind ? getReactionEmoji(activeKind) : "♡",
    total,
    liked: Boolean(activeKind || comment.liked),
  };
}

function normalizeCommentModel(item, index = 0) {
  const authorName = String(item?.authorName || "Пользователь").trim() || "Пользователь";
  const authorId =
    item?.authorUserId ??
    item?.authorId ??
    item?.userId ??
    item?.author?.userId ??
    item?.author?.id ??
    null;
  const seed = authorId ?? authorName ?? index;
  const rawId = item?.id ?? item?.commentId ?? `comment-${index}`;
  const rawParentId =
    item?.parentCommentId ??
    item?.parentId ??
    item?.parent_comment_id ??
    item?.parentComment?.id ??
    item?.parent?.id ??
    item?.replyToCommentId ??
    item?.replyTo?.id ??
    null;
  const rawDepth = Number(item?.depth ?? item?.level ?? item?.nestingLevel ?? 0);

  const reactionState = normalizeCommentReactionState(item);
  const activeKind = normalizeReactionKindForApi(reactionState.activeType, "");
  const totalReactions = sumReactionCounts(reactionState.counts);

  return {
    id: String(rawId),
    serverId: item?.id ?? null,
    parentCommentId: rawParentId == null || rawParentId === "" ? null : String(rawParentId),
    depth: Number.isFinite(rawDepth) && rawDepth > 0 ? rawDepth : 0,
    initials: getInitials(authorName),
    name: authorName,
    authorUserId: authorId,
    dateLabel: formatShortDate(item?.createdWhen || item?.createdAt) || "сейчас",
    text: String(item?.content || item?.text || "").trim(),
    likes: totalReactions,
    liked: Boolean(activeKind),
    myReactionKind: activeKind,
    reactionState,
    gradient: hashToGradient(seed, index),
    createdWhen: item?.createdWhen || item?.createdAt || null,
  };
}

function buildThreadedComments(list = []) {
  const ordered = Array.isArray(list) ? [...list] : [];
  const byId = new Map();
  const childrenByParent = new Map();
  const roots = [];

  ordered.forEach((item) => {
    if (!item?.id) return;
    byId.set(String(item.id), item);
  });

  ordered.forEach((item) => {
    if (!item?.id) return;
    const key = String(item.id);
    const parentKey = item?.parentCommentId ? String(item.parentCommentId) : null;

    if (parentKey && parentKey !== key && byId.has(parentKey)) {
      if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
      childrenByParent.get(parentKey).push(item);
      return;
    }
    roots.push(item);
  });

  const flat = [];
  const walk = (item, level = 0, parentId = null) => {
    const declaredDepth = Number(item?.depth ?? 0);
    const effectiveLevel = Math.max(
      0,
      Number.isFinite(declaredDepth) && declaredDepth > 0 ? Math.max(level, declaredDepth) : level
    );

    flat.push({
      ...item,
      renderDepth: effectiveLevel,
      renderParentId: parentId || item?.parentCommentId || null,
    });

    const children = childrenByParent.get(String(item.id)) || [];
    children.forEach((child) => walk(child, effectiveLevel + 1, String(item.id)));
  };

  roots.forEach((root) => walk(root, 0, null));
  return flat;
}

function resolveErrorText(error, fallback) {
  const direct = String(error?.message || "").trim();
  if (direct && !direct.startsWith("HTTP ")) return direct;
  const payloadMessage = String(error?.error || error?.detail || "").trim();
  if (payloadMessage) return payloadMessage;
  return fallback;
}

function parseIdSlugParam(raw) {
  const source = String(raw || "").trim();
  if (!source) return { id: null, slug: "" };
  const match = source.match(/^(\d+)-(.+)$/);
  if (match) return { id: Number(match[1]), slug: match[2] };
  return { id: null, slug: source };
}

function buildPostPath(post) {
  if (!post) return "/blog";
  const id = post.id ?? post.postId ?? null;
  const slug = String(post.slug || "").trim();
  if (id && slug) return `/blog/${id}-${slug}`;
  if (slug) return `/blog/${slug}`;
  if (id) return `/blog/${id}`;
  return "/blog";
}

function normalizeListResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data?.content)) return response.data.content;
  if (Array.isArray(response?.item?.content)) return response.item.content;
  return [];
}

function normalizeCatalogResponse(response) {
  if (!response || typeof response !== "object") return {};
  if (response.item && typeof response.item === "object") return response.item;
  return response;
}

function normalizePopularAuthorsResponse(source) {
  if (!Array.isArray(source)) return [];
  return source
    .map((item, index) => {
      const name = String(item?.name || item?.authorName || "").trim();
      if (!name) return null;
      const rawId = item?.authorUserId ?? item?.userId ?? item?.id ?? null;
      const userId = Number(rawId);
      const postsCount = Number(item?.postsCount ?? item?.articlesCount ?? 0);
      const subscribersCount = Number(item?.subscribersCount ?? item?.followersCount ?? 0);
      return {
        key: Number.isFinite(userId) && userId > 0 ? `author:${userId}` : `name:${name.toLowerCase()}`,
        userId: Number.isFinite(userId) && userId > 0 ? userId : null,
        name,
        initials: getInitials(name),
        postsCount: Number.isFinite(postsCount) && postsCount >= 0 ? postsCount : 0,
        subscribersCount: Number.isFinite(subscribersCount) && subscribersCount >= 0 ? subscribersCount : 0,
        subscribedByMe: Boolean(item?.subscribedByMe ?? item?.isSubscribed ?? item?.followed),
        avatarUrl: ensureAbsUrl(item?.avatarUrl || item?.avatar || ""),
        tone: index % DEFAULT_COMMENT_GRADIENTS.length,
      };
    })
    .filter(Boolean);
}

function normalizeCategory(post) {
  return String(post?.category || post?.categoryTitle || "").trim();
}

function categoryEmoji(category) {
  const map = {
    Тревожность: "🧠",
    Выгорание: "🔥",
    Отношения: "💬",
    Самооценка: "🧩",
    Травма: "🌱",
    Родительство: "🌙",
  };
  return map[category] || "✨";
}

function resolveAuthorName(post) {
  return String(post?.author?.name || post?.authorName || "Автор").trim();
}

function resolveAuthorUserId(post) {
  const raw = post?.author?.userId ?? post?.author?.id ?? post?.authorUserId ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function resolveAuthorSpec(post) {
  const parts = [];
  const headline = String(post?.author?.headline || post?.authorHeadline || post?.author?.specialization || "").trim();
  if (headline) parts.push(headline);

  const methods = Array.isArray(post?.author?.methods)
    ? post.author.methods
    : Array.isArray(post?.authorMethods)
      ? post.authorMethods
      : [];

  const firstMethod = String(methods[0] || "").trim();
  if (firstMethod) parts.push(firstMethod);

  const years = Number(post?.author?.experienceYears ?? post?.authorExperienceYears);
  if (Number.isFinite(years) && years > 0) parts.push(`${years}+ лет опыта`);

  return parts.join(" · ") || "—";
}

function resolveDisplayTags(post) {
  const tags = [];

  if (Array.isArray(post?.tags)) {
    tags.push(...post.tags);
  } else if (typeof post?.tags === "string") {
    try {
      const parsed = JSON.parse(post.tags);
      if (Array.isArray(parsed)) tags.push(...parsed);
      else tags.push(...post.tags.split(","));
    } catch {
      tags.push(...post.tags.split(","));
    }
  }

  if (post?.seo?.keywords) {
    tags.push(...String(post.seo.keywords).split(","));
  }

  return Array.from(
    new Set(
      tags
        .map((tag) => String(tag || "").trim())
        .filter(Boolean)
    )
  ).slice(0, 8);
}

function resolveLikesBase(post) {
  const value = Number(post?.likesCount ?? post?.reactionsCount ?? post?.favoritesCount);
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function getPostStatus(post = {}) {
  return String(post?.status || post?.publicationStatus || post?.state || post?.postStatus || "")
    .trim()
    .toUpperCase();
}

function isPostPublished(post = {}) {
  const token = getPostStatus(post);
  if (!token) return true;
  return token === "PUBLISHED" || token === "PUBLIC" || token === "PUBLISH";
}

function applyPublicPost(post) {
  if (!post || !isPostPublished(post)) return null;
  return post;
}

function processArticleHtml(rawHtml, titleFallback) {
  const source = String(rawHtml || "").trim();
  if (!source) return "";
  if (typeof DOMParser === "undefined") return source;

  const parser = new DOMParser();
  const doc = parser.parseFromString(source, "text/html");

  const stripControlChars = (value) => {
    const text = String(value || "");
    let out = "";
    for (let i = 0; i < text.length; i += 1) {
      const code = text.charCodeAt(i);
      if ((code >= 0 && code <= 31) || code === 127) continue;
      out += text[i];
    }
    return out;
  };

  const sanitizeMediaUrl = (raw) => {
    const value = stripControlChars(raw).trim();
    if (!value) return "";
    if (/^data:image\//i.test(value)) return value;
    if (/^blob:/i.test(value)) return value;
    const external = sanitizeExternalHttpUrl(value);
    if (external) return external;
    if (/^(\/|\.\/|\.\.\/)/.test(value)) return value;
    return "";
  };

  doc
    .querySelectorAll(
      "script,style,iframe,object,embed,link,meta,base,form,input,button,textarea,select,option,svg,math"
    )
    .forEach((node) => node.remove());

  doc.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const attrName = String(attr.name || "").toLowerCase();
      const attrValue = String(attr.value || "");
      if (!attrName) return;

      if (attrName.startsWith("on") || attrName === "style") {
        el.removeAttribute(attr.name);
        return;
      }

      if (attrName === "href") {
        const safeHref = sanitizeLinkHref(attrValue, {
          allowRelative: true,
          allowMailto: true,
          allowTel: true,
          allowHash: true,
        });
        if (!safeHref) {
          el.removeAttribute(attr.name);
          return;
        }
        el.setAttribute("href", safeHref);
        if (/^https?:\/\//i.test(safeHref)) {
          const rel = String(el.getAttribute("rel") || "").trim();
          const relParts = new Set(rel.split(/\s+/).filter(Boolean).map((s) => s.toLowerCase()));
          relParts.add("noopener");
          relParts.add("noreferrer");
          el.setAttribute("rel", Array.from(relParts).join(" "));
        }
        return;
      }

      if (attrName === "src" || attrName === "poster") {
        const safeSrc = sanitizeMediaUrl(attrValue);
        if (!safeSrc) {
          el.removeAttribute(attr.name);
          return;
        }
        el.setAttribute(attrName, safeSrc);
        return;
      }

      if (attrName === "srcset") {
        const safeSrcset = attrValue
          .split(",")
          .map((entry) => {
            const parts = entry.trim().split(/\s+/);
            const candidate = parts.shift() || "";
            const safeCandidate = sanitizeMediaUrl(candidate);
            if (!safeCandidate) return "";
            return [safeCandidate, ...parts].join(" ");
          })
          .filter(Boolean)
          .join(", ");
        if (!safeSrcset) {
          el.removeAttribute(attr.name);
          return;
        }
        el.setAttribute("srcset", safeSrcset);
        return;
      }

      if (attrName === "target") {
        const normalized = attrValue.trim().toLowerCase();
        if (normalized !== "_blank") {
          el.removeAttribute(attr.name);
          return;
        }
        el.setAttribute("target", "_blank");
      }
    });
  });

  doc.querySelectorAll("h1").forEach((node) => {
    const h2 = doc.createElement("h2");
    h2.innerHTML = node.innerHTML;
    node.replaceWith(h2);
  });

  doc.querySelectorAll("img").forEach((img) => {
    if (!img.getAttribute("loading")) img.setAttribute("loading", "lazy");
    if (!img.getAttribute("decoding")) img.setAttribute("decoding", "async");
    const alt = (img.getAttribute("alt") || "").trim();
    if (!alt) img.setAttribute("alt", titleFallback || "Изображение к статье");
  });

  const html = doc.body.innerHTML.trim();
  return html || "";
}

function trimForMeta(text, max = 170) {
  const normalized = String(text || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
}

function decodeHtmlEntities(value = "") {
  return String(value || "")
    .replace(/&amp;/gi, "&")
    .replace(/&#38;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function normalizeMediaCandidate(raw) {
  const value = decodeHtmlEntities(raw).trim();
  if (!value) return "";
  if (/^data:image\//i.test(value) || /^blob:/i.test(value)) return value;
  const abs = ensureAbsUrl(value);
  if (abs) return abs;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^(\/|\.\/|\.\.\/)/.test(value)) return value;
  return "";
}

function resolvePostCoverUrl(post) {
  const candidates = [
    post?.previewImageUrl,
    post?.coverImg,
    post?.coverUrl,
    post?.coverImageUrl,
    post?.imageUrl,
    post?.posterUrl,
    post?.thumbnailUrl,
    post?.seo?.ogImageUrl,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeMediaCandidate(candidate);
    if (normalized) return normalized;
  }
  return "";
}

export default function BlogPost() {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { me, isAuthenticated, role } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [post, setPost] = useState(null);

  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [popularAuthorsLoading, setPopularAuthorsLoading] = useState(false);
  const [popularAuthors, setPopularAuthors] = useState([]);
  const [popularAuthorFollowPending, setPopularAuthorFollowPending] = useState(() => new Set());

  const [liked, setLiked] = useState(false);
  const [likesCountOverride, setLikesCountOverride] = useState(null);
  const [postLikePending, setPostLikePending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [followPending, setFollowPending] = useState(false);
  const [copied, setCopied] = useState(false);

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [visibleComments, setVisibleComments] = useState(3);
  const [commentReactionPending, setCommentReactionPending] = useState(() => new Set());
  const [coverLoadError, setCoverLoadError] = useState(false);
  const showAuthorInfo = post?.showAuthor !== false;
  const commentsAllowed = post?.commentsEnabled !== false;

  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);
  const commentsRef = useRef(null);
  const commentsAnchorHandledRef = useRef("");
  const authorUserId = useMemo(() => resolveAuthorUserId(post), [post]);

  const reloadComments = React.useCallback(
    async (postId, { showLoader = true } = {}) => {
      if (!postId) {
        setComments([]);
        return [];
      }
      if (showLoader) setCommentsLoading(true);
      setCommentsError("");
      try {
        const items = await blogApi.listComments(postId);
        const normalized = (Array.isArray(items) ? items : [])
          .map((item, index) => normalizeCommentModel(item, index))
          .sort((a, b) => {
            const at = Date.parse(a?.createdWhen || 0) || 0;
            const bt = Date.parse(b?.createdWhen || 0) || 0;
            if (at !== bt) return at - bt;
            return String(a?.id || "").localeCompare(String(b?.id || ""));
          });
        setComments(normalized);
        return normalized;
      } catch (error) {
        setComments([]);
        setCommentsError(resolveErrorText(error, "Не удалось загрузить комментарии."));
        return [];
      } finally {
        if (showLoader) setCommentsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    async function loadPost() {
      setLoading(true);
      setNotFound(false);
      setPost(null);
      const rawKey = String(slugParam || "").trim();
      const parsed = parseIdSlugParam(rawKey);

      try {
        const response = applyPublicPost(await blogApi.getBySlug(rawKey));
        if (!mounted) return;
        setPost(response);
        setNotFound(!response);
      } catch {
        if (parsed?.slug && parsed.slug !== rawKey) {
          try {
            const fallback = applyPublicPost(await blogApi.getBySlug(parsed.slug));
            if (!mounted) return;
            setPost(fallback);
            setNotFound(!fallback);
          } catch {
            if (mounted) setNotFound(true);
          }
        } else if (mounted) {
          setNotFound(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPost();

    return () => {
      mounted = false;
    };
  }, [slugParam]);

  useEffect(() => {
    setLiked(Boolean(post?.likedByMe));
    setLikesCountOverride(null);
    setSaved(false);
    setFollowed(Boolean(post?.isBlogSubscribed ?? post?.isSubscribed));
    setFollowPending(false);
    setComments([]);
    setCommentsError("");
    setCommentsLoading(false);
    setCommentSubmitting(false);
    setCommentDraft("");
    setReplyTarget(null);
    setVisibleComments(3);
    setCopied(false);
    setPopularAuthors([]);
    setPopularAuthorFollowPending(new Set());
  }, [post?.id, post?.slug, post?.isBlogSubscribed, post?.isSubscribed, post?.likedByMe]);

  useEffect(() => {
    let mounted = true;
    if (!post?.id || !commentsAllowed) {
      setComments([]);
      setCommentsError("");
      return () => {
        mounted = false;
      };
    }

    reloadComments(post.id, { showLoader: true }).then(() => {
      if (!mounted) return;
    });

    return () => {
      mounted = false;
    };
  }, [post?.id, commentsAllowed, reloadComments]);

  useEffect(() => {
    let mounted = true;

    async function loadFollowStatus() {
      if (!isAuthenticated || !authorUserId || !showAuthorInfo) return;
      try {
        const ids = await blogApi.listAuthorSubscriptions();
        if (!mounted) return;
        const authorId = Number(authorUserId);
        setFollowed(Array.isArray(ids) ? ids.map(Number).includes(authorId) : false);
      } catch {
        // keep current state from payload fallback
      }
    }

    loadFollowStatus();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, authorUserId, showAuthorInfo]);

  useEffect(() => {
    let mounted = true;

    async function loadRelated() {
      if (!post?.id && !post?.slug) {
        setRelatedPosts([]);
        return;
      }
      if (!showAuthorInfo) {
        setRelatedPosts([]);
        return;
      }

      setRelatedLoading(true);
      try {
        const currentPostId = Number(post?.id);
        const currentPostSlug = String(post?.slug || "").trim().toLowerCase();
        const currentAuthorId = resolveAuthorUserId(post);

        if (currentAuthorId) {
          const response = await blogApi.listPublishedByAuthor({
            authorUserId: currentAuthorId,
            page: 0,
            size: 8,
            excludePostId: Number.isFinite(currentPostId) && currentPostId > 0 ? currentPostId : undefined,
          });
          if (!mounted) return;

          const byAuthor = normalizeListResponse(response)
            .filter((item) => {
              if (!item) return false;
              const itemId = Number(item?.id ?? item?.postId);
              if (Number.isFinite(currentPostId) && currentPostId > 0 && Number.isFinite(itemId) && itemId === currentPostId) {
                return false;
              }
              const itemSlug = String(item?.slug || "").trim().toLowerCase();
              if (currentPostSlug && itemSlug && itemSlug === currentPostSlug) return false;
              return true;
            })
            .slice(0, 3);

          setRelatedPosts(byAuthor);
          return;
        }

        const response = await blogApi.listPublished({ page: 0, size: 18 });
        if (!mounted) return;
        setRelatedPosts(
          normalizeListResponse(response)
            .filter((item) => item && item.id !== post.id && item.slug !== post.slug)
            .slice(0, 3)
        );
      } catch {
        if (mounted) setRelatedPosts([]);
      } finally {
        if (mounted) setRelatedLoading(false);
      }
    }

    loadRelated();

    return () => {
      mounted = false;
    };
  }, [post, showAuthorInfo]);

  useEffect(() => {
    let mounted = true;

    async function loadPopularAuthors() {
      if (!post?.id && !post?.slug) {
        setPopularAuthors([]);
        return;
      }

      setPopularAuthorsLoading(true);
      try {
        const response = await blogApi.getCatalog({ page: 0, size: 1, popularAuthorsLimit: 8, trendingLimit: 1 });
        if (!mounted) return;

        const catalog = normalizeCatalogResponse(response);
        const currentAuthorId = resolveAuthorUserId(post);
        const currentAuthorName = resolveAuthorName(post).toLowerCase();

        const realAuthors = normalizePopularAuthorsResponse(catalog?.popularAuthors)
          .filter((author) => {
            if (currentAuthorId && author.userId && author.userId === currentAuthorId) return false;
            if (!author.userId && author.name.toLowerCase() === currentAuthorName) return false;
            return true;
          })
          .slice(0, 3);

        setPopularAuthors(realAuthors);
      } catch {
        if (mounted) setPopularAuthors([]);
      } finally {
        if (mounted) setPopularAuthorsLoading(false);
      }
    }

    loadPopularAuthors();

    return () => {
      mounted = false;
    };
  }, [post]);

  useEffect(() => {
    const articleNode = articleRef.current;
    if (!articleNode) return undefined;

    const onScroll = () => {
      const rect = articleNode.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const total = Math.max(1, rect.height - viewport * 0.35);
      const passed = Math.min(Math.max(-rect.top + viewport * 0.18, 0), total);
      setProgress(Math.min(1, passed / total));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [post?.id, post?.slug]);

  const origin = useMemo(() => getSiteOrigin(), []);

  const canonicalUrl = useMemo(() => {
    if (!origin) return "";
    const path = post ? buildPostPath(post) : location.pathname || "/blog";
    try {
      return new URL(path, origin).toString();
    } catch {
      return "";
    }
  }, [location.pathname, origin, post]);

  const category = useMemo(() => normalizeCategory(post), [post]);
  const tags = useMemo(() => resolveDisplayTags(post), [post]);
  const roleToken = String(role || "").replace(/^ROLE_/i, "").toUpperCase();
  const canManageBlog = ["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(roleToken);
  const canRecommendClients = roleToken === "PSYCHOLOGIST";
  const showRecommendClient = canRecommendClients || canManageBlog;

  const articleHtml = useMemo(
    () => processArticleHtml(post?.content, post?.title),
    [post?.content, post?.title]
  );

  const articleText = useMemo(() => stripToText(articleHtml), [articleHtml]);

  const readingMinutes = useMemo(() => {
    const fromApi = Number(post?.readingTimeMinutes);
    if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
    return calcReadingMinutes(articleText);
  }, [post?.readingTimeMinutes, articleText]);
  const sectionCount = useMemo(() => {
    const matches = String(articleHtml || "").match(/<h[23]\b/gi);
    const count = Array.isArray(matches) ? matches.length : 0;
    return Math.max(1, Math.min(6, count || 3));
  }, [articleHtml]);

  const publishedAt = post?.publishedAt || post?.createdWhen || post?.createdAt || null;
  const dateLabel = formatDate(publishedAt);

  const authorName = useMemo(() => resolveAuthorName(post), [post]);
  const authorSpec = useMemo(() => resolveAuthorSpec(post), [post]);
  const authorInitials = useMemo(() => getInitials(authorName), [authorName]);
  const authorAvatarUrl = useMemo(
    () => ensureAbsUrl(post?.author?.avatar || post?.authorAvatarUrl || ""),
    [post?.author?.avatar, post?.authorAvatarUrl]
  );

  const likesBase = useMemo(() => resolveLikesBase(post), [post]);
  const likesCount = likesCountOverride == null ? likesBase : likesCountOverride;
  const commentsCount = commentsAllowed ? Math.max(Number(post?.commentsCount || 0), comments.length) : 0;
  const commentsById = useMemo(() => {
    const map = new Map();
    comments.forEach((comment) => {
      map.set(String(comment.id), comment);
    });
    return map;
  }, [comments]);
  const threadedComments = useMemo(() => buildThreadedComments(comments), [comments]);

  const shouldScrollToComments = useMemo(() => {
    if (!commentsAllowed) return false;
    const hash = String(location.hash || "").toLowerCase();
    const params = new URLSearchParams(location.search || "");
    if (hash === "#comments") return true;
    return params.get("focus") === "comments" || params.get("comments") === "1";
  }, [commentsAllowed, location.hash, location.search]);

  useEffect(() => {
    if (!shouldScrollToComments || !post?.id) return;
    const key = `${post.id}|${location.hash}|${location.search}`;
    if (commentsAnchorHandledRef.current === key) return;
    commentsAnchorHandledRef.current = key;
    const raf = window.requestAnimationFrame(() => {
      commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [shouldScrollToComments, post?.id, location.hash, location.search]);

  const title = String(post?.title || "Статья").trim();
  const description = trimForMeta(post?.seo?.description || post?.description || articleText, 180);
  const coverImageUrl = useMemo(() => resolvePostCoverUrl(post), [post]);
  const showCoverImage = Boolean(coverImageUrl) && !coverLoadError;

  const ogImage = useMemo(() => {
    const fromPost = normalizeMediaCandidate(post?.seo?.ogImageUrl) || coverImageUrl;
    if (fromPost) return fromPost;
    const env = String(process.env.REACT_APP_OG_IMAGE || "").trim();
    return ensureAbsUrl(env);
  }, [coverImageUrl, post?.seo?.ogImageUrl]);

  useEffect(() => {
    setCoverLoadError(false);
  }, [post?.id, post?.slug, coverImageUrl]);

  const shareUrl = canonicalUrl || (typeof window !== "undefined" ? window.location.href : "");

  const schema = useMemo(() => {
    if (!canonicalUrl || !title) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      headline: title,
      description,
      image: ogImage ? [ogImage] : undefined,
      datePublished: publishedAt ? new Date(publishedAt).toISOString() : undefined,
      dateModified: post?.lastModifiedWhen ? new Date(post.lastModifiedWhen).toISOString() : undefined,
      author: {
        "@type": "Person",
        name: authorName,
      },
      inLanguage: "ru-RU",
    };
  }, [authorName, canonicalUrl, description, ogImage, post?.lastModifiedWhen, publishedAt, title]);

  const authorStats = useMemo(() => {
    const postsCount = Number(post?.authorPostsCount ?? post?.author?.postsCount ?? post?.author?.blogPostsCount);
    const subscribersCount = Number(post?.authorSubscribersCount ?? post?.author?.subscribersCount ?? post?.author?.blogSubscribersCount);
    const rating = Number(post?.authorRating ?? post?.author?.rating ?? post?.averageRating);

    return {
      posts: Number.isFinite(postsCount) && postsCount >= 0 ? postsCount : 0,
      subscribers: Number.isFinite(subscribersCount) && subscribersCount >= 0 ? subscribersCount : 0,
      rating: Number.isFinite(rating) && rating > 0 ? rating.toFixed(1) : "—",
    };
  }, [post]);

  async function onCopyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  async function onShare() {
    try {
      if (navigator.share) {
        await navigator.share({ title, text: description, url: shareUrl });
        return;
      }
    } catch {
      // no-op
    }
    onCopyLink();
  }

  async function onTogglePostLike() {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!post?.id || postLikePending) return;

    try {
      setPostLikePending(true);
      const state = await blogApi.togglePostReaction(post.id);
      if (state && typeof state === "object") {
        if (typeof state.liked === "boolean") setLiked(state.liked);
        if (Number.isFinite(Number(state.reactionsCount))) {
          setLikesCountOverride(Number(state.reactionsCount));
        }
      }
    } catch {
      // keep current UI state
    } finally {
      setPostLikePending(false);
    }
  }

  async function onToggleCommentLike(commentId, reactionKind = DEFAULT_REACTION_KIND) {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const target = comments.find((item) => item.id === commentId);
    const serverId = target?.serverId ?? commentId;
    const normalizedKind = normalizeReactionKindForApi(reactionKind, DEFAULT_REACTION_KIND);
    if (!serverId) return;

    const pendingKey = String(commentId);
    if (commentReactionPending.has(pendingKey)) return;

    setCommentReactionPending((prev) => {
      const next = new Set(prev);
      next.add(pendingKey);
      return next;
    });

    try {
      const state = await blogApi.toggleCommentReaction(serverId, normalizedKind);
      if (!state || typeof state !== "object") return;

      setComments((prev) =>
        prev.map((item) => {
          if (item.id !== commentId) return item;
          const responseState = normalizeCommentReactionState({
            ...state.raw,
            reactionsCount: state.reactionsCount,
            liked: state.liked,
            active: state.reacted,
            myReactionKind: state.myReactionKind || state.reactionKind || (state.liked ? normalizedKind : ""),
          });
          const activeKind = normalizeReactionKindForApi(responseState.activeType, "");
          return {
            ...item,
            reactionState: responseState,
            myReactionKind: activeKind,
            liked: Boolean(activeKind),
            likes: sumReactionCounts(responseState.counts),
          };
        })
      );
    } catch {
      // keep current UI state
    } finally {
      setCommentReactionPending((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  }

  async function onToggleFollow() {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!authorUserId || followPending) return;

    try {
      setFollowPending(true);
      const state = await blogApi.toggleAuthorSubscription(authorUserId);
      if (state && typeof state === "object" && typeof state.isSubscribed === "boolean") {
        setFollowed(state.isSubscribed);
      } else {
        setFollowed((prev) => !prev);
      }
    } catch {
      // keep current state
    } finally {
      setFollowPending(false);
    }
  }

  async function onTogglePopularAuthorFollow(author) {
    if (!author?.userId) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const pendingKey = String(author.userId);
    if (popularAuthorFollowPending.has(pendingKey)) return;

    setPopularAuthorFollowPending((prev) => {
      const next = new Set(prev);
      next.add(pendingKey);
      return next;
    });

    try {
      const state = await blogApi.toggleAuthorSubscription(author.userId);
      const nextSubscribed = typeof state?.isSubscribed === "boolean" ? state.isSubscribed : !author.subscribedByMe;
      setPopularAuthors((prev) =>
        prev.map((item) => (item.userId === author.userId ? { ...item, subscribedByMe: nextSubscribed } : item))
      );
      if (authorUserId && author.userId === authorUserId) {
        setFollowed(nextSubscribed);
      }
    } catch {
      // keep current state
    } finally {
      setPopularAuthorFollowPending((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  }

  async function onSendComment() {
    if (!commentsAllowed) return;
    const text = String(commentDraft || "").trim();
    if (!text) return;

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!post?.id || commentSubmitting) return;

    try {
      setCommentSubmitting(true);
      const parentRawId = replyTarget?.serverId ?? replyTarget?.id ?? null;
      const payload = { content: text };
      if (parentRawId != null && parentRawId !== "") {
        const parentNumericId = Number(parentRawId);
        if (Number.isFinite(parentNumericId) && parentNumericId > 0) {
          payload.parentCommentId = parentNumericId;
          payload.parentId = parentNumericId;
          payload.replyToCommentId = parentNumericId;
        }
      }

      await blogApi.createComment(post.id, payload);
      const latest = await reloadComments(post.id, { showLoader: false });
      setVisibleComments((prev) => Math.max(prev, latest.length || 0));
      setCommentDraft("");
      setReplyTarget(null);
      setCommentsError("");
    } catch (error) {
      setCommentsError(resolveErrorText(error, "Не удалось отправить комментарий."));
    } finally {
      setCommentSubmitting(false);
    }
  }

  function onOpenChat() {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    navigate("/chat");
  }

  function onBook() {
    if (showAuthorInfo && authorUserId) {
      navigate(`/psychologist/${authorUserId}`);
      return;
    }
    navigate("/catalog");
  }

  function onOpenAuthorProfile(event, userId = authorUserId) {
    const path = buildPsychologistProfilePath(userId);
    if (!path) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    navigate(path);
  }

  function wrapWithAuthShell(content) {
    if (!isAuthenticated) return content;
    return (
      <div className="podcasts-hub b-blog-detail-auth">
        <div className="podcasts-shell">
          <AppSidebarV2
            activeKey="blog"
            mobileOpen={sidebarOpen}
            onCloseMobile={() => setSidebarOpen(false)}
          />

          <button
            className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
            type="button"
            aria-label="Закрыть меню"
            onClick={() => setSidebarOpen(false)}
          />

          <div className="podcasts-main">{content}</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return wrapWithAuthShell(
      <div className="b-blog-detail-v2">
        <div className="blogd-progress" aria-hidden="true">
          <div className="blogd-progress__bar" style={{ width: "18%" }} />
        </div>
        <div className="blogd-loading">
          <div className="blogd-loading__hero" />
          <div className="blogd-loading__body" />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return wrapWithAuthShell(
      <div className="b-blog-detail-v2">
        <Helmet>
          <title>Статья не найдена</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="blogd-empty">
          <h1>Статья не найдена</h1>
          <p>Возможно, материал был удалён или ссылка устарела.</p>
          <Link to="/blog" className="blogd-empty__back">
            ← Вернуться в блог
          </Link>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    const publishedDateTimeLabel = formatDateTime(publishedAt) || dateLabel || "—";
    const updatedDateTimeLabel = formatDateTime(post?.lastModifiedWhen || post?.updatedAt || post?.createdWhen) || "—";
    return wrapWithAuthShell(
      <div className="b-blog-detail-auth-v1">
        <Helmet prioritizeSeoTags>
          <title>{title}</title>
          {description ? <meta name="description" content={description} /> : null}
          {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
          <meta name="robots" content="index, follow" />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          {description ? <meta property="og:description" content={description} /> : null}
          {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
          {ogImage ? <meta property="og:image" content={ogImage} /> : null}
          <meta property="og:locale" content="ru_RU" />
          {ogImage ? <meta name="twitter:card" content="summary_large_image" /> : <meta name="twitter:card" content="summary" />}
          <meta name="twitter:title" content={title} />
          {description ? <meta name="twitter:description" content={description} /> : null}
          {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
          {schema ? <script type="application/ld+json">{JSON.stringify(schema)}</script> : null}
        </Helmet>

        <div className="blogd-auth-progress" aria-hidden="true">
          <div style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>

        <header className="topbar">
          <button className="blogd-menu-btn" type="button" aria-label="Открыть меню" onClick={() => setSidebarOpen(true)}>
            <span />
            <span />
            <span />
          </button>
          <div className="breadcrumb">
            <Link to="/blog">Блог</Link>
            <span className="bc-sep">›</span>
            {category ? (
              <>
                <Link to={`/blog?topic=${encodeURIComponent(category)}`}>{category}</Link>
                <span className="bc-sep">›</span>
              </>
            ) : null}
            <span className="bc-curr">{title}</span>
          </div>
          <div className="topbar-actions">
            <button className="tbtn tbtn-ghost" type="button" onClick={onShare}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {copied ? "Ссылка скопирована" : "Поделиться"}
            </button>
            {canManageBlog ? (
              <button className="tbtn tbtn-primary" type="button" onClick={() => navigate("/dashboard/blog/new")}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                Написать статью
              </button>
            ) : null}
          </div>
        </header>

        <div className="page-wrap">
          <div className="page-main">
            <div className="hero">
              <div className="h-orb-a" />
              <div className="h-orb-b" />
              <div className="h-inner">
                <div className="h-bc">
                  <Link to="/blog">Блог</Link>
                  <span className="h-bc-sep">›</span>
                  {category ? (
                    <>
                      <Link to={`/blog?topic=${encodeURIComponent(category)}`}>{category}</Link>
                      <span className="h-bc-sep">›</span>
                    </>
                  ) : null}
                  <span>Статья</span>
                </div>
                <div className="h-tags">
                  {category ? <span className="tag t-lav-d">{category}</span> : null}
                  <span className="tag t-dark">Статья</span>
                  {dateLabel ? <span className="tag t-dark">{dateLabel}</span> : null}
                </div>
                <h1 className="h-title">{title}</h1>
                {showAuthorInfo ? (
                  <div className="h-author">
                    {authorAvatarUrl ? (
                      <div className="h-av" aria-hidden="true"><img src={authorAvatarUrl} alt={authorName} /></div>
                    ) : (
                      <div className="h-av" aria-hidden="true">{authorInitials}</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div className="h-aname">{authorName}</div>
                      <div className="h-aspec">{authorSpec}</div>
                    </div>
                    <div className="h-abts">
                      <button className="btn-msg" type="button" onClick={onOpenChat}>Написать</button>
                      <button className={`btn-sub${followed ? " on" : ""}`} type="button" onClick={onToggleFollow} disabled={followPending}>
                        {followed ? "✓ Подписаны" : "Подписаться"}
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="h-insights">
                  <div className="h-insight"><b>{readingMinutes} мин</b><span>мягкое чтение без перегруза</span></div>
                  <div className="h-insight"><b>{sectionCount} шага</b><span>структурный разбор материала</span></div>
                  <div className="h-insight"><b>{commentsCount}</b><span>живых комментариев и ответы автора</span></div>
                </div>
                <div className="h-cover">
                  {showCoverImage ? (
                    <img
                      src={coverImageUrl}
                      alt={post?.seo?.imageAlt || title}
                      loading="eager"
                      decoding="async"
                      fetchpriority="high"
                      onError={() => setCoverLoadError(true)}
                    />
                  ) : (
                    <div className="h-cover-em poster">{categoryEmoji(category)}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="content-wrap">
              <div className="art-card">
                <div className="art-meta">
                  <time>{dateLabel || "Без даты"}</time>
                  <div className="art-meta-dot" />
                  <span>{readingMinutes} мин чтения</span>
                  <div className="art-meta-dot" />
                  <span>♡ {likesCount}</span>
                  <div className="art-meta-dot" />
                  <span>💬 {commentsCount} комментариев</span>
                  <div className="art-meta-dates">
                    <div className="art-date-item">
                      <span className="art-date-label">Опубликовано:</span>
                      <time className="art-date-val">{publishedDateTimeLabel}</time>
                    </div>
                    <div className="art-date-sep">·</div>
                    <div className="art-date-item">
                      <span className="art-date-label">Обновлено:</span>
                      <time className="art-date-val">{updatedDateTimeLabel}</time>
                    </div>
                  </div>
                </div>

                <div className="art" ref={articleRef}>
                  <div className="blogd-article-prose" dangerouslySetInnerHTML={{ __html: articleHtml }} />
                </div>

                <div className="article-toolkit">
                  <button className="tool-card" type="button" onClick={() => setSaved((prev) => !prev)}>
                    <div className="tool-card-ico">🔖</div><b>{saved ? "Сохранено" : "Сохранить"}</b><span>Вернуться к материалу позже.</span>
                  </button>
                  {showRecommendClient ? (
                    <button className="tool-card" type="button" onClick={() => navigate("/dashboard/clients")}>
                      <div className="tool-card-ico">🤝</div><b>Клиенту</b><span>Отправить статью как домашнюю практику.</span>
                    </button>
                  ) : null}
                  {commentsAllowed ? (
                    <button className="tool-card" type="button" onClick={() => commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                      <div className="tool-card-ico">💬</div><b>Обсудить</b><span>Задать вопрос автору или поделиться опытом.</span>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="post-actions">
                <button type="button" className={`pab${liked ? " liked" : ""}`} onClick={onTogglePostLike} disabled={postLikePending} aria-label="Нравится">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{likesCount}</span>
                </button>
                {commentsAllowed ? (
                  <button type="button" className="pab" onClick={() => commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} aria-label="К комментариям">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{commentsCount}</span>
                  </button>
                ) : null}
                <div className="pab-sep" />
                <button type="button" className={`pab${saved ? " saved" : ""}`} onClick={() => setSaved((prev) => !prev)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{saved ? "Сохранено" : "Сохранить"}</span>
                </button>
                <button type="button" className="pab" onClick={onShare}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <span>{copied ? "Скопировано" : "Поделиться"}</span>
                </button>
                <div className="pab-sep" />
                {showRecommendClient ? (
                  <button type="button" className="pab-recommend" onClick={() => navigate("/dashboard/clients")}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                      <path d="M16 11l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Рекомендовать клиенту
                  </button>
                ) : null}
                <div className="pab-spacer" />
                <button type="button" className="pab-book" onClick={onBook}>Записаться →</button>
              </div>

              {commentsAllowed ? (
              <div className="cmt-card" ref={commentsRef}>
                <div className="cmt-head">
                  <div className="cmt-head-left">Комментарии <span className="cmt-cnt">{commentsCount}</span></div>
                  <div className="cmt-sort">
                    <button className="cmt-sort-btn on" type="button">Новые</button>
                    <button className="cmt-sort-btn" type="button">Популярные</button>
                  </div>
                </div>

                <div className="cmt-insight">
                  <div className="cmt-insight-ico">✨</div>
                  <div>
                    <b>В обсуждении часто спрашивают про практические шаги</b>
                    <span>Автор отвечает в комментариях, можно продолжить диалог ниже.</span>
                  </div>
                </div>

                <div className="ci-row">
                  <div className="ci-av">{getInitials(me?.name || me?.email || "Вы")}</div>
                  <div className="ci-inner">
                    {replyTarget ? (
                      <div className="blogd-reply-target cmt-reply-target">
                        <span>Ответ для <b>{replyTarget.name}</b></span>
                        <button type="button" onClick={() => setReplyTarget(null)} disabled={commentSubmitting}>Отменить</button>
                      </div>
                    ) : null}
                    <textarea
                      className="ci-inp"
                      value={commentDraft}
                      onChange={(event) => setCommentDraft(event.target.value)}
                      placeholder="Напишите комментарий…"
                      rows={2}
                      disabled={commentSubmitting}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          event.preventDefault();
                          onSendComment();
                        }
                      }}
                    />
                    <div className="ci-foot">
                      <div className="ci-tools" />
                      <button className="ci-send" type="button" onClick={onSendComment} disabled={commentSubmitting}>
                        {commentSubmitting ? "Отправка…" : "Отправить →"}
                      </button>
                    </div>
                  </div>
                </div>

                {commentsError ? <div className="blogd-comments-error">{commentsError}</div> : null}
                {commentsLoading ? <div className="blogd-comments-loading">Загружаем комментарии…</div> : null}
                {!commentsLoading && !threadedComments.length ? <div className="blogd-comments-empty">Пока нет комментариев. Будьте первым.</div> : null}

                {!commentsLoading && threadedComments.slice(0, visibleComments).map((comment) => {
                  const parent = comment?.renderParentId ? commentsById.get(String(comment.renderParentId)) : null;
                  const isReply = Number(comment.renderDepth || 0) > 0;
                  const depth = Math.min(Math.max(Number(comment.renderDepth || 0), 0), 6);
                  const reactionView = buildCommentReactionView(comment);
                  const reactionPending = commentReactionPending.has(String(comment.id));
                  return (
                    <div key={comment.id}>
                      <div className={`cmt${isReply ? ` cmt--reply cmt--depth-${depth}` : ""}`}>
                        <div className="cmt-av" style={{ background: comment.gradient }}>{comment.initials}</div>
                        <div className="cmt-body">
                          <div className="cmt-hdr"><span className="cmt-name">{comment.name}</span><span className="cmt-date">{comment.dateLabel}</span></div>
                          {parent ? <div className="cmt-replyto">↳ Ответ для {parent.name}</div> : null}
                          <div className="cmt-txt">{comment.text}</div>
                          <div className="cmt-acts">
                            <div className="cmt-reaction-wrap">
                              <button
                                type="button"
                                className={`cmt-act cmt-reaction-main${reactionView.liked ? " liked" : ""}`}
                                onClick={() => onToggleCommentLike(comment.id, reactionView.activeKind || DEFAULT_REACTION_KIND)}
                                disabled={reactionPending}
                                title={reactionView.liked ? "Убрать реакцию" : "Поставить реакцию"}
                              >
                                <span className="cmt-reaction-emoji">{reactionView.emoji}</span>
                                <span>{reactionView.total}</span>
                              </button>
                              <div className="cmt-reaction-menu" role="group" aria-label="Выбрать реакцию">
                                {REACTION_OPTIONS.map((option) => {
                                  const count = Math.max(0, Number(comment.reactionState?.counts?.[option.kind] || 0) || 0);
                                  const active = reactionView.activeKind === option.kind;
                                  return (
                                    <button
                                      key={`${comment.id}-${option.kind}`}
                                      type="button"
                                      className={`cmt-reaction-option${active ? " is-active" : ""}`}
                                      onClick={() => onToggleCommentLike(comment.id, option.kind)}
                                      disabled={reactionPending}
                                      title={option.label}
                                    >
                                      <span>{option.emoji}</span>
                                      {count ? <b>{count}</b> : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="cmt-act"
                              onClick={() => {
                                setReplyTarget({ id: comment.id, serverId: comment.serverId, name: comment.name });
                                setCommentDraft((prev) => (prev ? prev : `@${comment.name}, `));
                              }}
                            >
                              ↩ Ответить
                            </button>
                            <ReportContentButton
                              targetType="BLOG_COMMENT"
                              targetId={comment.serverId || comment.id}
                              title={title}
                              contentSnippet={comment.text}
                              className="cmt-act cmt-act--report"
                              compact
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {!commentsLoading && threadedComments.length > visibleComments ? (
                  <button
                    type="button"
                    className="cmt-more"
                    onClick={() => setVisibleComments((prev) => prev + 3)}
                  >
                    Показать ещё
                  </button>
                ) : null}
              </div>
              ) : null}
            </div>
          </div>

          <div className="page-sbar page-sbar--auth">
            {showAuthorInfo ? (
            <div className="sbar-card">
              <div className="sb-lbl">Об авторе</div>
              <div className="ac-head">
                <div className="ac-av">{authorAvatarUrl ? <img src={authorAvatarUrl} alt={authorName} /> : authorInitials}</div>
                <div><div className="ac-name">{authorName}</div><div className="ac-spec">{authorSpec}</div></div>
              </div>
              <div className="ac-bio">{post?.authorBio || "—"}</div>
              <div className="ac-stats">
                <div className="ac-stat"><b>{authorStats.posts}</b><span>публикации</span></div>
                <div className="ac-stat"><b>{authorStats.subscribers}</b><span>подписчики</span></div>
                <div className="ac-stat"><b>{authorStats.rating}★</b><span>рейтинг</span></div>
              </div>
              <button className={`ac-btn-f${followed ? " on" : ""}`} type="button" onClick={onToggleFollow} disabled={followPending}>
                {followed ? "✓ Подписаны" : "Подписаться на блог"}
              </button>
              <button className="ac-btn-b" type="button" onClick={onBook}>Записаться на сессию</button>
            </div>
            ) : null}

            {showAuthorInfo ? (
            <div className="sbar-card">
              <div className="sb-lbl">Другие статьи автора</div>
              {relatedLoading ? (
                <div className="blogd-related-skeleton" />
              ) : relatedPosts.length ? (
                relatedPosts.map((item) => (
                  <Link key={item.id || item.slug} to={buildPostPath(item)} className="rel">
                    <div className={`rel-cov${resolvePostCoverUrl(item) ? " has-image" : ""}`}>
                      {resolvePostCoverUrl(item) ? (
                        <img src={resolvePostCoverUrl(item)} alt={item.title || "Обложка статьи"} loading="lazy" decoding="async" />
                      ) : (
                        <span className="rel-em">{categoryEmoji(normalizeCategory(item))}</span>
                      )}
                    </div>
                    <div className="rel-info">
                      <div className="rel-ttl">{item.title}</div>
                      <div className="rel-meta"><span>{Number(item?.readingTimeMinutes) > 0 ? `${item.readingTimeMinutes} мин` : "—"}</span><span>♡ {resolveLikesBase(item)}</span></div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="blogd-related-empty">Пока нет других опубликованных материалов.</div>
              )}
            </div>
            ) : null}

            {popularAuthorsLoading ? (
              <div className="sbar-card">
                <div className="sb-lbl">Популярные авторы</div>
                <div className="blogd-related-skeleton" />
              </div>
            ) : popularAuthors.length ? (
              <div className="sbar-card">
                <div className="sb-lbl">Популярные авторы</div>
                {popularAuthors.map((item, index) => (
                  <div key={item.key} className="pa">
                    <div className={`pa-av pa-av--tone-${item.tone ?? index % DEFAULT_COMMENT_GRADIENTS.length}`}>
                      {item.avatarUrl ? <img src={item.avatarUrl} alt={item.name} loading="lazy" decoding="async" /> : item.initials}
                    </div>
                    <div className="pa-info"><div className="pa-name">{item.name}</div><div className="pa-cnt">{item.postsCount} постов</div></div>
                    <button
                      type="button"
                      className={`pa-follow${item.subscribedByMe ? " is-on" : ""}`}
                      onClick={() => onTogglePopularAuthorFollow(item)}
                      disabled={!item.userId || popularAuthorFollowPending.has(String(item.userId))}
                    >
                      {item.subscribedByMe ? "✓ Подписаны" : "+ Следить"}
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return wrapWithAuthShell(
    <div className={`b-blog-detail-v2${isAuthenticated ? " b-blog-detail-v2--auth" : ""}`}>
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        {description ? <meta name="description" content={description} /> : null}
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        {description ? <meta property="og:description" content={description} /> : null}
        {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta property="og:locale" content="ru_RU" />
        {ogImage ? <meta name="twitter:card" content="summary_large_image" /> : <meta name="twitter:card" content="summary" />}
        <meta name="twitter:title" content={title} />
        {description ? <meta name="twitter:description" content={description} /> : null}
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        {schema ? <script type="application/ld+json">{JSON.stringify(schema)}</script> : null}
      </Helmet>

      <div className="blogd-progress" aria-hidden="true">
        <div className="blogd-progress__bar" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>

      <section className="blogd-hero">
        <div className="blogd-hero__orb-a" />
        <div className="blogd-hero__orb-b" />

        <div className="blogd-hero__inner">
          <nav className="blogd-breadcrumbs" aria-label="Хлебные крошки">
            <Link to="/blog">Блог</Link>
            <span className="blogd-breadcrumbs__sep">›</span>
            {category ? (
              <>
                <Link to={`/blog?topic=${encodeURIComponent(category)}`}>{category}</Link>
                <span className="blogd-breadcrumbs__sep">›</span>
              </>
            ) : null}
            <span className="blogd-breadcrumbs__current">{title}</span>
          </nav>

          <div className="blogd-tags">
            {category ? <span className="blogd-tag blogd-tag--lav">{category}</span> : null}
            <span className="blogd-tag blogd-tag--dark">Статья</span>
            {dateLabel ? <span className="blogd-tag blogd-tag--dark">{dateLabel}</span> : null}
          </div>

          <h1 className="blogd-title">{title}</h1>

          {showAuthorInfo ? (
          <div className="blogd-hero-author">
            {authorUserId ? (
              <button
                type="button"
                className="b-psych-link-btn"
                onClick={(event) => onOpenAuthorProfile(event, authorUserId)}
              >
                <div className="blogd-hero-author__avatar" aria-hidden="true">
                  {authorAvatarUrl ? <img src={authorAvatarUrl} alt={authorName} /> : authorInitials}
                </div>
                <div className="blogd-hero-author__info">
                  <div className="blogd-hero-author__name">{authorName}</div>
                  <div className="blogd-hero-author__spec">{authorSpec}</div>
                </div>
              </button>
            ) : (
              <>
                <div className="blogd-hero-author__avatar" aria-hidden="true">
                  {authorAvatarUrl ? <img src={authorAvatarUrl} alt={authorName} /> : authorInitials}
                </div>
                <div className="blogd-hero-author__info">
                  <div className="blogd-hero-author__name">{authorName}</div>
                  <div className="blogd-hero-author__spec">{authorSpec}</div>
                </div>
              </>
            )}

            <div className="blogd-hero-author__actions">
              <button type="button" className="blogd-btn-msg" onClick={onOpenChat}>
                Написать
              </button>
              <button
                type="button"
                className={`blogd-btn-sub${followed ? " is-on" : ""}`}
                onClick={onToggleFollow}
                disabled={followPending}
              >
                {followed ? "✓ Подписаны" : "Подписаться"}
              </button>
            </div>
          </div>
          ) : null}

          {isAuthenticated ? (
            <div className="blogd-hero-insights">
              <div className="blogd-hero-insight">
                <b>{readingMinutes} мин</b>
                <span>мягкое чтение без перегруза</span>
              </div>
              <div className="blogd-hero-insight">
                <b>{sectionCount} шага</b>
                <span>структурный разбор материала</span>
              </div>
              <div className="blogd-hero-insight">
                <b>{commentsCount}</b>
                <span>комментариев и ответы автора</span>
              </div>
            </div>
          ) : null}

          <div className="blogd-cover">
            {showCoverImage ? (
              <img
                src={coverImageUrl}
                alt={post?.seo?.imageAlt || title}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                onError={() => setCoverLoadError(true)}
              />
            ) : (
              <div className="blogd-cover__fallback" aria-hidden="true">
                {categoryEmoji(category)}
              </div>
            )}

            <div className="blogd-cover__time">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {readingMinutes} мин чтения
            </div>
          </div>
        </div>
      </section>

      <div className="blogd-page">
        <div className="blogd-main">
          <article className="blogd-article-card">
            <div className="blogd-article-meta">
              <span>{dateLabel || "Без даты"}</span>
              <span className="blogd-meta-dot" />
              <span>{readingMinutes} мин чтения</span>
              <span className="blogd-meta-dot" />
              <span>♡ {likesCount}</span>
              <span className="blogd-meta-dot" />
              <span>💬 {commentsCount} комментариев</span>
            </div>

            <div
              className="blogd-article-prose"
              ref={articleRef}
              dangerouslySetInnerHTML={{ __html: articleHtml }}
            />
          </article>

          <div className="blogd-actions">
            <button
              type="button"
              className={`blogd-action-btn${liked ? " is-liked" : ""}`}
              onClick={onTogglePostLike}
              disabled={postLikePending}
              aria-label="Нравится"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{likesCount}</span>
            </button>

            {commentsAllowed ? (
              <button
                type="button"
                className="blogd-action-btn"
                onClick={() => commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                aria-label="Комментарии"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                <span>{commentsCount}</span>
              </button>
            ) : null}

            <span className="blogd-action-sep" aria-hidden="true" />

            <button
              type="button"
              className={`blogd-action-btn${saved ? " is-saved" : ""}`}
              onClick={() => setSaved((prev) => !prev)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              <span>{saved ? "Сохранено" : "Сохранить"}</span>
            </button>

            <button type="button" className="blogd-action-btn" onClick={onShare}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span>{copied ? "Ссылка скопирована" : "Поделиться"}</span>
            </button>

            {isAuthenticated ? (
              <button
                type="button"
                className="blogd-recommend-btn"
                onClick={() => navigate("/dashboard/clients")}
              >
                Рекомендовать клиенту
              </button>
            ) : null}

            <ReportContentButton
              targetType="BLOG_POST"
              targetId={post.id}
              title={title}
              contentSnippet={description || stripToText(articleHtml)}
              className="blogd-action-btn blogd-action-btn--report"
            />

            <button type="button" className="blogd-book-btn" onClick={onBook}>
              {showAuthorInfo ? `Записаться к ${authorName.split(" ")[0]} →` : "Выбрать психолога →"}
            </button>
          </div>

          {commentsAllowed ? (
          <section id="comments" className="blogd-comments" ref={commentsRef}>
            <h2 className="blogd-comments__title">
              Комментарии <span>{commentsCount}</span>
            </h2>

            <div className="blogd-comment-input-row">
              <div className="blogd-comment-avatar" aria-hidden="true">
                {getInitials(me?.name || me?.email || "Вы")}
              </div>

              <div className="blogd-comment-input-col">
                {replyTarget ? (
                  <div className="blogd-reply-target">
                    <span>
                      Ответ для <b>{replyTarget.name}</b>
                    </span>
                    <button
                      type="button"
                      onClick={() => setReplyTarget(null)}
                      disabled={commentSubmitting}
                    >
                      Отменить
                    </button>
                  </div>
                ) : null}

                <textarea
                  className="blogd-comment-input"
                  value={commentDraft}
                  onChange={(event) => setCommentDraft(event.target.value)}
                  placeholder="Напишите комментарий…"
                  rows={2}
                  disabled={commentSubmitting}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                      event.preventDefault();
                      onSendComment();
                    }
                  }}
                />

                <button type="button" className="blogd-comment-send" onClick={onSendComment} disabled={commentSubmitting}>
                  {commentSubmitting ? "Отправка…" : "Отправить →"}
                </button>
              </div>
            </div>

            {commentsError ? <div className="blogd-comments-error">{commentsError}</div> : null}

            {commentsLoading ? <div className="blogd-comments-loading">Загружаем комментарии…</div> : null}

            {!commentsLoading && !comments.length ? (
              <div className="blogd-comments-empty">Пока нет комментариев. Будьте первым.</div>
            ) : null}

            {!commentsLoading &&
              threadedComments.slice(0, visibleComments).map((comment) => {
                const parent = comment?.renderParentId ? commentsById.get(String(comment.renderParentId)) : null;
                return (
                <div key={comment.id}>
                  <div
                    className={`blogd-comment-item${comment.renderDepth > 0 ? " is-nested" : ""}`}
                    style={{ marginLeft: `${Math.min(Math.max(comment.renderDepth || comment.depth || 0, 0), 6) * 26}px` }}
                  >
                    <div className="blogd-comment-item__avatar" style={{ background: comment.gradient }} aria-hidden="true">
                      {comment.initials}
                    </div>

                    <div className="blogd-comment-item__body">
                      <div className="blogd-comment-item__head">
                        {comment.authorUserId ? (
                          <button
                            type="button"
                            className="b-psych-link-btn"
                            onClick={(event) => onOpenAuthorProfile(event, comment.authorUserId)}
                          >
                            <strong>{comment.name}</strong>
                          </button>
                        ) : (
                          <strong>{comment.name}</strong>
                        )}
                        <span>{comment.dateLabel}</span>
                      </div>

                      {parent ? <div className="blogd-comment-item__replyto">↳ Ответ для {parent.name}</div> : null}
                      <p>{comment.text}</p>

                      <div className="blogd-comment-item__actions">
                        <button
                          type="button"
                          className={`blogd-comment-act${comment.liked ? " is-liked" : ""}`}
                          onClick={() => onToggleCommentLike(comment.id)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>{comment.likes}</span>
                        </button>

                        <button
                          type="button"
                          className="blogd-comment-act"
                          onClick={() => {
                            setReplyTarget({
                              id: comment.id,
                              serverId: comment.serverId,
                              name: comment.name,
                            });
                            setCommentDraft((prev) => (prev ? prev : `@${comment.name}, `));
                          }}
                        >
                          Ответить
                        </button>

                        <ReportContentButton
                          targetType="BLOG_COMMENT"
                          targetId={comment.serverId || comment.id}
                          title={title}
                          contentSnippet={comment.text}
                          className="blogd-comment-act blogd-comment-act--report"
                          compact
                        />
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}

            {!commentsLoading && threadedComments.length > visibleComments ? (
              <button
                type="button"
                className="blogd-comments-more"
                onClick={() => setVisibleComments((prev) => prev + 3)}
              >
                Показать ещё
              </button>
            ) : null}
          </section>
          ) : null}
        </div>

        <aside className="blogd-sidebar">
          {showAuthorInfo ? (
          <section className="blogd-sidebar-card">
            <div className="blogd-side-label">Об авторе</div>

            <div className="blogd-author-card-head">
              <div className="blogd-author-card-avatar" aria-hidden="true">
                {authorAvatarUrl ? <img src={authorAvatarUrl} alt={authorName} /> : authorInitials}
              </div>

              <div>
                <div className="blogd-author-card-name">
                  {authorUserId ? (
                    <button
                      type="button"
                      className="b-psych-link-btn"
                      onClick={(event) => onOpenAuthorProfile(event, authorUserId)}
                    >
                      {authorName}
                    </button>
                  ) : (
                    authorName
                  )}
                </div>
                <div className="blogd-author-card-spec">{authorSpec}</div>
              </div>
            </div>

            <p className="blogd-author-card-bio">
              {post?.authorBio || "—"}
            </p>

            <div className="blogd-author-card-stats">
              <div>
                <b>{authorStats.posts}</b>
                <span>публикации</span>
              </div>
              <div>
                <b>{authorStats.subscribers}</b>
                <span>подписчики</span>
              </div>
              <div>
                <b>{authorStats.rating}★</b>
                <span>рейтинг</span>
              </div>
            </div>

            <button type="button" className="blogd-author-card-follow" onClick={onToggleFollow} disabled={followPending}>
              {followed ? "✓ Подписаны на блог" : "Подписаться на блог"}
            </button>

            <button type="button" className="blogd-author-card-book" onClick={onBook}>
              Записаться на сессию
            </button>
          </section>
          ) : null}

          {showAuthorInfo ? (
          <section className="blogd-sidebar-card">
            <div className="blogd-side-label">Другие статьи автора</div>

            {relatedLoading ? (
              <div className="blogd-related-skeleton" />
            ) : relatedPosts.length ? (
              relatedPosts.map((item) => (
                <Link key={item.id || item.slug} to={buildPostPath(item)} className="blogd-related-item">
                  <div className={`blogd-related-item__cover${resolvePostCoverUrl(item) ? " has-image" : ""}`} aria-hidden="true">
                    {resolvePostCoverUrl(item) ? (
                      <img src={resolvePostCoverUrl(item)} alt={item.title || "Обложка статьи"} loading="lazy" decoding="async" />
                    ) : (
                      categoryEmoji(normalizeCategory(item))
                    )}
                  </div>

                  <div className="blogd-related-item__info">
                    <div className="blogd-related-item__title">{item.title}</div>
                    <div className="blogd-related-item__meta">
                      <span>{Number(item?.readingTimeMinutes) > 0 ? `${item.readingTimeMinutes} мин` : "—"}</span>
                      <span>♡ {resolveLikesBase(item)}</span>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="blogd-related-empty">Пока нет других опубликованных материалов.</div>
            )}
          </section>
          ) : null}

          {popularAuthorsLoading ? (
            <section className="blogd-sidebar-card">
              <div className="blogd-side-label">Популярные авторы</div>
              <div className="blogd-related-skeleton" />
            </section>
          ) : popularAuthors.length ? (
            <section className="blogd-sidebar-card">
              <div className="blogd-side-label">Популярные авторы</div>

              {popularAuthors.map((item, index) => (
                <div key={item.key} className="blogd-popular-author">
                  <div
                    className={`blogd-popular-author__avatar blogd-popular-author__avatar--tone-${item.tone ?? index % DEFAULT_COMMENT_GRADIENTS.length}`}
                    aria-hidden="true"
                  >
                    {item.avatarUrl ? <img src={item.avatarUrl} alt={item.name} loading="lazy" decoding="async" /> : item.initials}
                  </div>

                  <div className="blogd-popular-author__info">
                    <div className="blogd-popular-author__name">
                      {item.userId ? (
                        <button
                          type="button"
                          className="b-psych-link-btn"
                          onClick={(event) => onOpenAuthorProfile(event, item.userId)}
                        >
                          {item.name}
                        </button>
                      ) : (
                        item.name
                      )}
                    </div>
                    <div className="blogd-popular-author__count">{item.postsCount} постов</div>
                  </div>

                  <button
                    type="button"
                    className={`blogd-popular-author__follow${item.subscribedByMe ? " is-on" : ""}`}
                    onClick={() => onTogglePopularAuthorFollow(item)}
                    disabled={!item.userId || popularAuthorFollowPending.has(String(item.userId))}
                  >
                    {item.subscribedByMe ? "✓ Подписаны" : "+ Следить"}
                  </button>
                </div>
              ))}
            </section>
          ) : null}
        </aside>
      </div>

      {tags.length ? (
        <div className="blogd-bottom-tags" aria-label="Теги статьи">
          {tags.map((tag) => (
            <Link key={tag} to={`/blog?q=${encodeURIComponent(tag)}`} className="blogd-bottom-tag">
              #{tag}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
